import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Check, ChevronDown } from 'lucide-react';
import { numberOptions, pickMatches } from '../logic/numberedPick';

/**
 * A long dropdown you can type your way through.
 *
 * Built for the ticket form's 46 issue types, which staff were scrolling while
 * a customer waited. Two ways in:
 *   · the NUMBER beside each option — permanent, so it becomes muscle memory;
 *   · WORDS, forgiving of spelling and word order.
 *
 * Falls back to a plain list when nothing is typed, so it is never worse than
 * the dropdown it replaces.
 */
const NumberedSearchSelect = ({
    options,                 // string[] or {value,label}[]
    value,
    onChange,
    placeholder = 'Type a number or a few letters…',
    id,
}) => {
    const all = useMemo(() => numberOptions(options || []), [options]);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [cursor, setCursor] = useState(0);
    const boxRef = useRef(null);
    const inputRef = useRef(null);

    const { rows, exactNumber } = useMemo(() => pickMatches(all, query), [all, query]);
    const selected = all.find((o) => o.value === value) || null;

    useEffect(() => { setCursor(0); }, [query]);

    // Close when clicking elsewhere.
    useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDocDown);
        return () => document.removeEventListener('mousedown', onDocDown);
    }, [open]);

    useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

    const choose = (opt) => {
        if (!opt) return;
        onChange?.(opt.value);
        setQuery('');
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, rows.length - 1)); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); return; }
        if (e.key === 'Escape') { setOpen(false); return; }
        if (e.key === 'Enter') {
            e.preventDefault();
            // A typed number picks that option outright, even if the list below
            // still shows its siblings (typing "1" lists 1 and 10-19).
            choose(exactNumber || rows[cursor]);
        }
    };

    return (
        <div className="relative" ref={boxRef}>
            <button
                type="button" id={id}
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-left hover:border-slate-400"
            >
                <span className={selected ? 'text-slate-800' : 'text-slate-400'}>
                    {selected ? <><span className="text-slate-400 font-mono mr-1.5">{selected.number}.</span>{selected.label}</> : 'Select…'}
                </span>
                <ChevronDown size={15} className="text-slate-400 shrink-0" />
            </button>

            {open && (
                <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                    <div className="relative border-b border-slate-100">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder={placeholder}
                            className="w-full pl-8 pr-3 py-2.5 text-sm outline-none"
                        />
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                        {rows.length === 0 && (
                            <div className="px-3 py-4 text-sm text-slate-400 text-center">
                                Nothing matches “{query}”.
                            </div>
                        )}
                        {rows.map((o, i) => (
                            <button
                                key={o.value}
                                type="button"
                                onMouseEnter={() => setCursor(i)}
                                onClick={() => choose(o)}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === cursor ? 'bg-purple-50' : ''} ${o.value === value ? 'font-semibold text-purple-800' : 'text-slate-700'}`}
                            >
                                <span className="font-mono text-[11px] text-slate-400 w-7 shrink-0">{o.number}.</span>
                                <span className="flex-1">{o.label}</span>
                                {o.value === value && <Check size={14} className="text-purple-600 shrink-0" />}
                            </button>
                        ))}
                    </div>
                    <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400">
                        Type a number, or a few letters — spelling need not be exact. ↑↓ then Enter.
                    </div>
                </div>
            )}
        </div>
    );
};

export default NumberedSearchSelect;
